import { createRouter, createWebHistory } from "vue-router";
import store from "../store";
import { MedicalDept } from "@/enums/common.enums";
import { RouterEnum } from "@/router/router.enums";
import { i18n } from "@/main"; // Import the i18n instance
import { localeRouterUtils } from "@/locales/locale.router";
import alimtalkChannelService from "@/services/alimtalkChannel.service";
import { isStudioEmbed, getStudioAccessToken, endStudioSession } from "@/utils/studioEmbed";
import { clearSupport, clearTokens, getAccessToken, getProfile, isSupportEnded, removeProfile, setAccessToken } from "@/utils/tokenStore";
import { setAuditScreen } from "@/utils/clientHeaders";
import { message } from "ant-design-vue";
import { permState, loadPerms, reloadPerms } from "@/services/permState";
import { routeDenial, MENU_DENIED_TEXT } from "@/utils/permUi";
import { permissionDeniedText } from "@/utils/standardErrors";

// 지원 접속 진입·끝·앱 사이 이동 받기 화면(로그인 검사·회사 정보 불러오기 밖)
const SupportEnter = () =>
  import(/* webpackChunkName: "SupportEnter" */ "../views/SupportEnter.vue");
const HandoffEnter = () =>
  import(/* webpackChunkName: "HandoffEnter" */ "../views/HandoffEnter.vue");
const OPEN_ENTRY_PATHS = [
  RouterEnum.SupportEnter.path,
  RouterEnum.SupportEnded.path,
  RouterEnum.HandoffEnter.path,
];

const Dashboard = () =>
  import(
    /* webpackChunkName: "Dashboard" */ "../views/Dashboard/Dashboard.vue"
  );
const BrandSetting = () =>
  import(
    /* webpackChunkName: "BrandSetting" */ "../views/BrandManagement/BrandSetting.vue"
  );
const ShareLinksPage = () =>
  import(
    /* webpackChunkName: "ShareLinksPage" */ "../views/ShareLinks/ShareLinksPage.vue"
  );
const ShareLinkStatsPage = () =>
  import(
    /* webpackChunkName: "ShareLinkStatsPage" */ "../views/ShareLinks/ShareLinkStatsPage.vue"
  );
const DentIssueCertificate = () =>
  import(
    /* webpackChunkName: "DentIssueCertificate" */ "../views/IssueCertificateV2/DentIssueCertificate.vue"
  );
// const DentIssueCertificateOsstem = () => import(/* webpackChunkName: "DentIssueCertificateOsstem" */ '../views/IssueCertificate/backup/DentIssueCertificateOsstem.vue');

const DentIssueCertificateZh = () =>
  import(
    /* webpackChunkName: "DentIssueCertificateZh" */ "../views/IssueCertificate/Zh/DentIssueCertificate.vue"
  );

// const OphIssueCertificate = () => import(/* webpackChunkName: "OphIssueCertificate" */ '../views/IssueCertificate/OphIssueCertificate.vue');
const OphIssueCertificate = () =>
  import(
    /* webpackChunkName: "OphIssueCertificateV2" */ "../views/IssueCertificateV2/OphIssueCertificate.vue"
  );
const PsIssueCertificate = () =>
  import(
    /* webpackChunkName: "PsIssueCertificate" */ "../views/IssueCertificate/PsIssueCertificate.vue"
  );
const DermIssueCertificate = () =>
  import(
    /* webpackChunkName: "DermIssueCertificate" */ "../views/IssueCertificate/DermIssueCertificate.vue"
  );
const ImIssueCertificate = () =>
  import(
    /* webpackChunkName: "ImIssueCertificate" */ "../views/IssueCertificate/ImIssueCertificate.vue"
  );
const OsIssueCertificate = () =>
  import(
    /* webpackChunkName: "OsIssueCertificate" */ "../views/IssueCertificate/OsIssueCertificate.vue"
  );
const OsEditCertificateContents = () =>
  import(
    /* webpackChunkName: "OsEditCertificateContents" */ "../views/CertificateSetting/Os.vue"
  );
const CommonIssueCertificate = () =>
  import(
    /* webpackChunkName: "CommonIssueCertificate" */ "../views/IssueCertificate/CommonIssueCertificate.vue"
  );
const CommonEditCertificateContents = () =>
  import(
    /* webpackChunkName: "CommonEditCertificateContents" */ "../views/CertificateSetting/Common.vue"
  );

const VetIssueCertificate = () =>
  import(
    /* webpackChunkName: "VetIssueCertificate" */ "../views/IssueCertificate/VetIssueCertificate.vue"
  );
const VetEditCertificateContents = () =>
  import(
    /* webpackChunkName: "VetEditCertificateContents" */ "../views/CertificateSetting/Vet.vue"
  );

const OphBulkIssue = () =>
  import(
    /* webpackChunkName: "OphBulkIssue" */ "../views/IssueCertificate/OphBulkIssueCertificate.vue"
  );
// SPEC-CERTAPI-ZEISS-001 design §7.2 — 엑셀 일괄 발행 새 화면(작업 접수·진행 조회). 아래 안과 대량 발행 라우트의
// 분기 한 곳(VUE_APP_CERTIFICATE_API_BULK_URL 이 채워졌을 때만)에서만 열린다. 옛 화면 파일은 손대지 않는다.
const OphBulkIssueJob = () =>
  import(
    /* webpackChunkName: "OphBulkIssueJob" */ "../views/IssueCertificate/OphBulkIssueCertificateJob.vue"
  );
const PsEditCertificateContents = () =>
  import(
    /* webpackChunkName: "PsEditCertificateContents" */ "../views/CertificateSetting/Ps.vue"
  );

const DentEditCertificateContentsList = () =>
  import(
    /* webpackChunkName: "DentEditCertificateContents" */ "../views/CertificateSetting/Ko/Dent/DentList.vue"
  );
const DentEditCertificateContents = () =>
  import(
    /* webpackChunkName: "DentEditCertificateContents" */ "../views/CertificateSetting/Ko/Dent/Implant.vue"
  );
const DentBraceEditCertificateContents = () =>
  import(
    /* webpackChunkName: "DentEditCertificateContents" */ "../views/CertificateSetting/Ko/Dent/Brace.vue"
  );
const DentAbutmentEditCertificateContents = () =>
  import(
    /* webpackChunkName: "DentAbutmentEditCertificateContents" */ "../views/CertificateSetting/Ko/Dent/Abutment.vue"
  );
const DentZirconiaEditCertificateContents = () =>
  import(
    /* webpackChunkName: "DentZirconiaEditCertificateContents" */ "../views/CertificateSetting/Ko/Dent/Zirconia.vue"
  );
const DentEditCertificateContentsZh = () =>
  import(
    /* webpackChunkName: "DentEditCertificateContentsZh" */ "../views/CertificateSetting/Zh/Dent.vue"
  );
const DentEditCertificateContentsJa = () =>
  import(
    /* webpackChunkName: "DentEditCertificateContentsJa" */ "../views/CertificateSetting/Ja/Dent.vue"
  );

const OphEditCertificateContents = () =>
  import(
    /* webpackChunkName: "OphEditCertificateContents" */ "../views/CertificateSetting/Oph.vue"
  );
const DermEditCertificateContents = () =>
  import(
    /* webpackChunkName: "DermEditCertificateContents" */ "../views/CertificateSetting/Derm.vue"
  );
const ImEditCertificateContents = () =>
  import(
    /* webpackChunkName: "ImEditCertificateContents" */ "../views/CertificateSetting/Im.vue"
  );
const OrderContainer = () =>
  import(
    /* webpackChunkName: "OrderContainer" */ "../views/Order/OrderContainer.vue"
  );
const CertificateList = () =>
  import(
    /* webpackChunkName: "CertificateList" */ "../views/History/CertificateList.vue"
  );
const MobileCertificateList = () =>
  import(
    /* webpackChunkName: "MobileCertificateList" */ "../views/History/MobileCertificateList.vue"
  );
const CustomerList = () =>
  import(
    /* webpackChunkName: "CustomerList" */ "../views/History/CustomerList.vue"
  );
const CustomerDetails = () =>
  import(
    /* webpackChunkName: "CustomerDetails" */ "../views/History/CustomerDetails.vue"
  );
const HospitalInfo = () =>
  import(
    /* webpackChunkName: "HospitalInfo" */ "../views/HospitalInfo/HospitalInfo.vue"
  );
const InfoPassword = () =>
  import(
    /* webpackChunkName: "HospitalPw" */ "../views/HospitalInfo/HospitalPw.vue"
  );
const InfoEdit = () =>
  import(
    /* webpackChunkName: "HospitalEdit" */ "../views/HospitalInfo/HospitalEdit.vue"
  );
const Store = () =>
  import(/* webpackChunkName: "Store" */ "../views/Store/Store.vue");
const SubscriptionList = () =>
  import(
    /* webpackChunkName: "SubscriptionList" */ "../views/SalesRecordManagement/subscription/SubscriptionList.vue"
  );
const MobileNoticeHospital = () =>
  import(
    /* webpackChunkName: "MobileNoticeHospital" */ "../views/Notice/MobileNoticeHospital.vue"
  );
const MobileNoticeCustomer = () =>
  import(
    /* webpackChunkName: "MobileNoticeCustomer" */ "../views/Notice/MobileNoticeCustomer.vue"
  );
const NoticeList = () =>
  import(/* webpackChunkName: "NoticeList" */ "../views/Notice/NoticeList.vue");
const NoticeRead = () =>
  import(/* webpackChunkName: "NoticeRead" */ "../views/Notice/NoticeRead.vue");

const Signin = () =>
  import(/* webpackChunkName: "Signin" */ "../views/Signin/Signin.vue");
const SigninSetIdAndPw = () =>
  import(
    /* webpackChunkName: "SigninSetIdAndPw" */ "../views/Signin/SetIdAndPw.vue"
  );
const SigninFindId = () =>
  import(/* webpackChunkName: "SigninFindId" */ "../views/Signin/FindId.vue");
const SigninFindIdDone = () =>
  import(
    /* webpackChunkName: "SigninFindIdDone" */ "../views/Signin/FindIdDone.vue"
  );
const SigninResetPwdAuth = () =>
  import(
    /* webpackChunkName: "SigninResetPwdAuth" */ "../views/Signin/ResetPwdAuth.vue"
  );
const SigninResetPwd = () =>
  import(
    /* webpackChunkName: "SigninResetPwd" */ "../views/Signin/ResetPwd.vue"
  );
const SigninResetPwdDone = () =>
  import(
    /* webpackChunkName: "SigninResetPwdDone" */ "../views/Signin/ResetPwdDone.vue"
  );

const SignupMain = () =>
  import(/* webpackChunkName: "SignupMain" */ "../views/Signup/SignupMain.vue");
const SignupComplete = () =>
  import(
    /* webpackChunkName: "SignupComplete" */ "../views/Signup/SignupComplete.vue"
  );

const GoToApp = () =>
  import(/* webpackChunkName: "GoToApp" */ "../views/GoToApp.vue");
const ContractList = () =>
  import(
    /* webpackChunkName: "ContractList" */ "../views/ContractManagement/ContractList.vue"
  );

const ReservationList = () =>
  import(
    /* webpackChunkName: "ContractList" */ "../views/ReservationManagement/ReservationList.vue"
  );
const ReviewManagement = () =>
  import(
    /* webpackChunkName: "ReviewManagement" */ "../views/Review/ReviewManagement.vue"
  );

const LaborContractList = () =>
  import(
    /* webpackChunkName: "LaborContractList" */ "../views/ContractManagement/labor/LaborContractList.vue"
  );

// 시술동의서 관련 컴포넌트 import 추가
const ConsentContractList = () =>
  import(
    /* webpackChunkName: "ConsentContractList" */ "../views/ContractManagement/consent/ConsentContractList.vue"
  );
const ConsentContractTemplateList = () =>
  import(
    /* webpackChunkName: "ConsentContractTemplateList" */ "../views/ContractManagement/consent/ConsentContractTemplateList.vue"
  );
const ImplantConsent = () =>
  import(
    /* webpackChunkName: "ImplantConsent" */ "../views/ContractManagement/consent/forms/ImplantConsent.vue"
  );

// 서버 점검 페이지 컴포넌트 import 추가
const Maintenance = () =>
  import(
    /* webpackChunkName: "Maintenance" */ "../views/Maintenance/Maintenance.vue"
  );

const BrowserNotification = () =>
  import(
    /* webpackChunkName: "BrowserNotification" */ "../views/BrowserNotification/BrowserNotification.vue"
  );
const AlimtalkChannelSettings = () =>
  import(
    /* webpackChunkName: "AlimtalkChannelSettings" */ "../views/AlimtalkChannelSettings/AlimtalkChannelSettings.vue"
  );
const AlimtalkTemplateCreate = () =>
  import(
    /* webpackChunkName: "AlimtalkTemplateCreate" */ "../views/Alimtalk/TemplateCreate.vue"
  );
const AlimtalkTemplateList = () =>
  import(
    /* webpackChunkName: "AlimtalkTemplateList" */ "../views/Alimtalk/TemplateList.vue"
  );
const AlimtalkTemplateBulkCreate = () =>
  import(
    /* webpackChunkName: "AlimtalkTemplateBulkCreate" */ "../views/Alimtalk/TemplateBulkCreate.vue"
  );
const AlimtalkTemplateEdit = () =>
  import(
    /* webpackChunkName: "AlimtalkTemplateEdit" */ "../views/Alimtalk/TemplateEdit.vue"
  );

const SubAccountList = () =>
  import(
    /* webpackChunkName: "SubAccountList" */ "../views/SubAccountManagement/SubAccountList.vue"
  );

const routes = [
  { path: "/studio-embed", redirect: RouterEnum.Dashboard.path },
  { name: RouterEnum.SupportEnter.name, path: RouterEnum.SupportEnter.path, component: SupportEnter },
  { name: RouterEnum.SupportEnded.name, path: RouterEnum.SupportEnded.path, component: SupportEnter },
  { name: RouterEnum.HandoffEnter.name, path: RouterEnum.HandoffEnter.path, component: HandoffEnter },
  {
    path: RouterEnum.KakaoTalk.path,
    name: RouterEnum.KakaoTalk.name,
  },
  {
    path: "/",
    name: "/",
    redirect: getAccessToken()
      ? RouterEnum.Dashboard.path
      : RouterEnum.Signin.path,
  },
  /*

        TODO: signin/find-id 이런 식으로 뜨게끔

    */
  // 로그인
  {
    name: RouterEnum.Signin.name,
    path: RouterEnum.Signin.path,
    redirect: RouterEnum.Signin.path,
    children: [
      {
        name: RouterEnum.Signin.name,
        path: "",
        component: Signin,
      },
      {
        name: RouterEnum.Signin.SetIdAndPw.name,
        path: RouterEnum.Signin.SetIdAndPw.path,
        component: SigninSetIdAndPw,
      },
      {
        name: RouterEnum.Signin.FindId.name,
        path: RouterEnum.Signin.FindId.path,
        component: SigninFindId,
      },
      {
        name: RouterEnum.Signin.FindIdDone.name,
        path: RouterEnum.Signin.FindIdDone.path,
        component: SigninFindIdDone,
      },
      {
        name: RouterEnum.Signin.ResetPwdAuth.name,
        path: RouterEnum.Signin.ResetPwdAuth.path,
        component: SigninResetPwdAuth,
      },
      {
        name: RouterEnum.Signin.ResetPwd.name,
        path: RouterEnum.Signin.ResetPwd.path,
        component: SigninResetPwd,
      },
      {
        name: RouterEnum.Signin.ResetPwdDone.name,
        path: RouterEnum.Signin.ResetPwdDone.path,
        component: SigninResetPwdDone,
      },
    ],
  },
  {
    name: RouterEnum.Signup.name,
    path: RouterEnum.Signup.path,
    redirect: RouterEnum.Signup.path,
    children: [
      {
        name: RouterEnum.Signup.name,
        path: "",
        component: SignupMain,
      },
      {
        name: RouterEnum.Signup.Complete.name,
        path: RouterEnum.Signup.Complete.path,
        component: SignupComplete,
      },
    ],
  },
  {
    name: RouterEnum.Dashboard.name,
    path: RouterEnum.Dashboard.path,
    // component: process.env.NODE_ENV === 'development' ? DashboardNew : Dashboard,
    component: Dashboard,
  },
  {
    // 병원 소개 페이지 설정
    name: RouterEnum.BrandSetting.name,
    path: RouterEnum.BrandSetting.path,
    component: BrandSetting,
  },
  {
    // 공유 링크 관리
    name: 'ShareLinks',
    path: '/share-links',
    component: ShareLinksPage,
  },
  {
    // 공유 링크 통계
    name: 'ShareLinkStats',
    path: '/share-links/:code/stats',
    component: ShareLinkStatsPage,
  },

  // 정형외과 인증서 발행 메뉴
  {
    name: RouterEnum.Os.name,
    path: RouterEnum.Os.path,
    redirect: `${RouterEnum.Os.path}/${RouterEnum.OsIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.OsIssueCertificate.name,
        path: RouterEnum.OsIssueCertificate.path,
        component: OsIssueCertificate,
      },
      {
        name: RouterEnum.OsEditCertificateContents.name,
        path: RouterEnum.OsEditCertificateContents.path,
        component: OsEditCertificateContents,
      },
    ],
  },

  // 공통 인증서 발행 메뉴
  {
    name: RouterEnum.Common.name,
    path: RouterEnum.Common.path,
    redirect: `${RouterEnum.Common.path}/${RouterEnum.CommonIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.CommonIssueCertificate.name,
        path: RouterEnum.CommonIssueCertificate.path,
        component: CommonIssueCertificate,
      },
      {
        name: RouterEnum.CommonEditCertificateContents.name,
        path: RouterEnum.CommonEditCertificateContents.path,
        component: CommonEditCertificateContents,
      },
    ],
  },
  // 반려동물 인증서 발행 메뉴
  {
    name: RouterEnum.Vet.name,
    path: RouterEnum.Vet.path,
    redirect: `${RouterEnum.Vet.path}/${RouterEnum.VetIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.VetIssueCertificate.name,
        path: RouterEnum.VetIssueCertificate.path,
        component: VetIssueCertificate,
      },
      {
        name: RouterEnum.VetEditCertificateContents.name,
        path: RouterEnum.VetEditCertificateContents.path,
        component: VetEditCertificateContents,
      },
    ],
  },
  // 치과전용메뉴
  {
    name: RouterEnum.Dent.name,
    path: RouterEnum.Dent.path,
    redirect: `${RouterEnum.Dent.path}/${RouterEnum.DentIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.DentIssueCertificate.name,
        path: RouterEnum.DentIssueCertificate.path,
        meta: {
          requiresDynamicComponent: true,
        },
        component: DentIssueCertificate,
      },
      {
        name: RouterEnum.DentEditCertificateContentsList.name,
        path: RouterEnum.DentEditCertificateContentsList.path,
        meta: {
          requiresDynamicComponent: true,
        },
        component: DentEditCertificateContentsList,
      },
      {
        name: RouterEnum.DentEditCertificateContents.name,
        path: RouterEnum.DentEditCertificateContents.path,
        component: DentEditCertificateContents,
      },
      {
        name: RouterEnum.DentBraceEditCertificateContents.name,
        path: RouterEnum.DentBraceEditCertificateContents.path,
        component: DentBraceEditCertificateContents,
      },
      {
        name: RouterEnum.DentAbutmentEditCertificateContents.name,
        path: RouterEnum.DentAbutmentEditCertificateContents.path,
        component: DentAbutmentEditCertificateContents,
      },
      {
        name: RouterEnum.DentZirconiaEditCertificateContents.name,
        path: RouterEnum.DentZirconiaEditCertificateContents.path,
        component: DentZirconiaEditCertificateContents,
      },
      // 중국치과 - V2로 리다이렉트 (작업지시서 기능 통합)
      {
        name: `${RouterEnum.DentIssueCertificate.name}${RouterEnum.Zh.name}`,
        path: `${RouterEnum.DentIssueCertificate.path}/${RouterEnum.Zh.path}`,
        redirect: `/${RouterEnum.Dent.path}/${RouterEnum.DentIssueCertificate.path}`,
      },
      {
        name: `${RouterEnum.DentEditCertificateContents.name}${RouterEnum.Zh.name}`,
        path: `${RouterEnum.DentEditCertificateContents.path}/${RouterEnum.Zh.path}`,
        component: DentEditCertificateContentsZh,
      },

      {
        name: `${RouterEnum.DentEditCertificateContents.name}${RouterEnum.Ja.name}`,
        path: `${RouterEnum.DentEditCertificateContents.path}/${RouterEnum.Ja.path}`,
        component: DentEditCertificateContentsJa,
      },
    ],
  },
  // 안과인증서발행메뉴들(추후수정예정)
  {
    name: RouterEnum.Oph.name,
    path: RouterEnum.Oph.path,
    redirect: `${RouterEnum.Oph.path}/${RouterEnum.OphIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.OphIssueCertificate.name,
        path: RouterEnum.OphIssueCertificate.path,
        component: OphIssueCertificate,
      },
      {
        name: RouterEnum.OphBulkIssue.name,
        path: RouterEnum.OphBulkIssue.path,
        // 엑셀 설정값이 비면 옛 화면 그대로(AC-110), 채워지면 새 작업 흐름 화면(design §7.2)
        component: process.env.VUE_APP_CERTIFICATE_API_BULK_URL
          ? OphBulkIssueJob
          : OphBulkIssue,
      },
      {
        name: RouterEnum.OphEditCertificateContentsList.name,
        path: RouterEnum.OphEditCertificateContentsList.path,
        component: OphEditCertificateContents,
      },
    ],
  },
  // 성형외과인증서발행메뉴들(추후수정예정)
  {
    name: RouterEnum.Ps.name,
    path: RouterEnum.Ps.path,
    redirect: `${RouterEnum.Ps.path}/${RouterEnum.PsIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.PsIssueCertificate.name,
        path: RouterEnum.PsIssueCertificate.path,
        component: PsIssueCertificate,
      },
      {
        name: RouterEnum.PsEditCertificateContents.name,
        path: `${RouterEnum.PsEditCertificateContents.path}/:step`,
        component: PsEditCertificateContents,
      },
    ],
  },
  // 피부과인증서발행메뉴들(추후수정예정)
  {
    name: RouterEnum.Derm.name,
    path: RouterEnum.Derm.path,
    redirect: `${RouterEnum.Derm.path}/${RouterEnum.DermIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.DermIssueCertificate.name,
        path: RouterEnum.DermIssueCertificate.path,
        component: DermIssueCertificate,
      },
      {
        name: RouterEnum.DermEditCertificateContents.name,
        path: `${RouterEnum.DermEditCertificateContents.path}/:step`,
        component: DermEditCertificateContents,
      },
    ],
  },
  // 내과인증서발행메뉴들
  {
    name: RouterEnum.Im.name,
    path: RouterEnum.Im.path,
    redirect: `${RouterEnum.Im.path}/${RouterEnum.ImIssueCertificate.path}`,
    children: [
      {
        name: RouterEnum.ImIssueCertificate.name,
        path: RouterEnum.ImIssueCertificate.path,
        component: ImIssueCertificate,
      },
      {
        name: RouterEnum.ImEditCertificateContents.name,
        path: `${RouterEnum.ImEditCertificateContents.path}/:step`,
        component: ImEditCertificateContents,
      },
    ],
  },

  {
    // 덴티움 전용
    name: RouterEnum.Order.Container.name,
    path: RouterEnum.Order.Container.path,
    component: OrderContainer,
  },

  {
    // 계약관리
    path: RouterEnum.ContractManagement.path,
    name: RouterEnum.ContractManagement.name,
    children: [
      {
        path: RouterEnum.ContractManagement.ContractList.path,
        name: RouterEnum.ContractManagement.ContractList.name,
        component: ContractList,
      },
    ],
  },
  {
    // 상품 구매
    name: RouterEnum.Store.name,
    path: RouterEnum.Store.path,
    component: Store,
  },
  {
    // 발행내역 조회
    name: RouterEnum.CertificateList.name,
    path: RouterEnum.CertificateList.path,
    component: CertificateList,
  },
  {
    // 고객관리
    name: RouterEnum.CustomerList.name,
    path: RouterEnum.CustomerList.path,
    component: CustomerList,
  },
  {
    // 고객 상세
    name: RouterEnum.CustomerDetails.name,
    path: RouterEnum.CustomerDetails.path,
    component: CustomerDetails,
  },
  {
    // 병원 정보 관리
    name: RouterEnum.HospitalInfo.name,
    path: RouterEnum.HospitalInfo.path,
    component: HospitalInfo,
  },
  {
    // 병원 정보 비밀번호
    name: RouterEnum.InfoPassword.name,
    path: RouterEnum.InfoPassword.path,
    component: InfoPassword,
  },
  {
    // 병원 정보 수정
    name: RouterEnum.InfoEdit.name,
    path: RouterEnum.InfoEdit.path,
    component: InfoEdit,
  },
  {
    // 렌탈 내역
    name: RouterEnum.Subscription.name,
    path: RouterEnum.Subscription.path,
    component: SubscriptionList,
  },
  {
    name: RouterEnum.Notices.name,
    path: RouterEnum.Notices.path,
    redirect: `${RouterEnum.Notices.path}/${RouterEnum.NoticeList.path}`,
    children: [
      {
        name: RouterEnum.NoticeList.name,
        path: RouterEnum.NoticeList.path,
        component: NoticeList,
      },
      {
        name: RouterEnum.NoticeRead.name,
        path: `:id`, // Include the id parameter in the path
        component: NoticeRead,
      },
    ],
  },
  // 모바일 앱용 공지사항, 환자용 공지사항
  {
    name: RouterEnum.MobilePages.name,
    path: RouterEnum.MobilePages.path,
    children: [
      {
        name: RouterEnum.MobileNoticeHospital.name,
        path: RouterEnum.MobileNoticeHospital.path,
        component: MobileNoticeHospital,
      },
      {
        name: RouterEnum.MobileNoticeCustomer.name,
        path: RouterEnum.MobileNoticeCustomer.path,
        component: MobileNoticeCustomer,
      },
    ],
  },
  {
    name: RouterEnum.GoToApp.name,
    path: RouterEnum.GoToApp.path,
    component: GoToApp,
  },
  // 토큰이 필요한 모바일 페이지들
  {
    // 발행내역 조회 모바일
    name: RouterEnum.MobilePagesWithToken.name,
    path: RouterEnum.MobilePagesWithToken.path,
    children: [
      {
        name: RouterEnum.MobileCertificateList.name,
        path: RouterEnum.MobileCertificateList.path,
        component: MobileCertificateList,
      },
    ],
  },
  // 병원 예약
  {
    name: RouterEnum.ReservationList.name,
    path: RouterEnum.ReservationList.path,
    component: ReservationList,
  },
  // 후기 관리
  {
    name: RouterEnum.ReviewManagement.name,
    path: RouterEnum.ReviewManagement.path,
    component: ReviewManagement,
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: getAccessToken()
      ? RouterEnum.Dashboard.path
      : RouterEnum.Signin.path,
  },
  {
    // 전자서명 관리
    path: RouterEnum.ElectronicSign.path,
    name: RouterEnum.ElectronicSign.name,
    children: [
      {
        path: RouterEnum.ElectronicSign.LaborContract.path,
        name: RouterEnum.ElectronicSign.LaborContract.name,
        component: LaborContractList,
      },
      {
        path: RouterEnum.ElectronicSign.LaborContractCreate.path,
        name: RouterEnum.ElectronicSign.LaborContractCreate.name,
        component: () =>
          import("@/views/ContractManagement/labor/LaborContractCreate.vue"),
      },
      {
        path: RouterEnum.ElectronicSign.ConsentContract.path,
        name: RouterEnum.ElectronicSign.ConsentContract.name,
        component: ConsentContractList,
      },
      {
        path: RouterEnum.ElectronicSign.ConsentContractTemplate.path,
        name: RouterEnum.ElectronicSign.ConsentContractTemplate.name,
        component: ConsentContractTemplateList,
      },
      {
        path: RouterEnum.ElectronicSign.ConsentContractCreate.path,
        name: RouterEnum.ElectronicSign.ConsentContractCreate.name,
        component: ImplantConsent,
      },
    ],
  },
  // 서버 점검 페이지 라우트 추가
  {
    name: RouterEnum.Maintenance.name,
    path: RouterEnum.Maintenance.path,
    component: Maintenance,
    meta: {
      allowDuringMaintenance: true, // 서버 점검 중에도 접근 가능하도록 설정
    },
  },
  {
    name: RouterEnum.BrowserNotification.name,
    path: RouterEnum.BrowserNotification.path,
    component: BrowserNotification,
  },
  {
    name: RouterEnum.Alimtalk.name,
    path: RouterEnum.Alimtalk.path,
    redirect: `${RouterEnum.Alimtalk.path}/${RouterEnum.Alimtalk.ChannelSettings.path}`,
    children: [
      {
        name: RouterEnum.Alimtalk.ChannelSettings.name,
        path: RouterEnum.Alimtalk.ChannelSettings.path,
        component: AlimtalkChannelSettings,
      },
      {
        name: RouterEnum.Alimtalk.TemplateCreate.name,
        path: RouterEnum.Alimtalk.TemplateCreate.path,
        component: AlimtalkTemplateCreate,
        meta: { requiresPrimaryChannel: true },
      },
      {
        name: RouterEnum.Alimtalk.TemplateList.name,
        path: RouterEnum.Alimtalk.TemplateList.path,
        component: AlimtalkTemplateList,
        meta: { requiresPrimaryChannel: true },
      },
      {
        name: RouterEnum.Alimtalk.TemplateBulkCreate.name,
        path: RouterEnum.Alimtalk.TemplateBulkCreate.path,
        component: AlimtalkTemplateBulkCreate,
        meta: { requiresPrimaryChannel: true },
      },
      {
        name: RouterEnum.Alimtalk.TemplateEdit.name,
        path: RouterEnum.Alimtalk.TemplateEdit.path,
        component: AlimtalkTemplateEdit,
        meta: { requiresPrimaryChannel: true },
      },
    ],
  },
  {
    name: RouterEnum.SubAccountManagement.name,
    path: RouterEnum.SubAccountManagement.path,
    redirect: `${RouterEnum.SubAccountManagement.path}/${RouterEnum.SubAccountManagement.List.path}`,
    children: [
      {
        name: RouterEnum.SubAccountManagement.List.name,
        path: RouterEnum.SubAccountManagement.List.path,
        component: SubAccountList,
      },
    ],
  },
];

/**
 * 지금 받아 둔 권한으로 이 주소를 막나(permUi.routeDenial — 사이드바와 같은 판정). 가드와 화면 안 버튼이 같이 씀.
 * 회사 정보를 아직 못 받았으면 옛 규칙으로는 막지 않음(관리자·권한 있는 직원의 버튼이 깜빡 사라지지 않게)
 */
export const routeDenialNow = (path) =>
  routeDenial({
    path,
    legacy: store.state.accountPermission?.accountType != null ? store.getters.getPermissions : null,
    perms: permState.perms,
    modes: permState.modes,
    locale: i18n.global.locale,
  });

/** 화면 안 배너·버튼·링크: 갈 수 없는 화면이면 숨김(누르면 「권한 없음」으로 튕기는 막다른 길 방지) */
export const canOpen = (path) => !routeDenialNow(path);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

const MOBILE_PATHS = [RouterEnum.MobilePages.path, "/dev"];
const ACCESSTOKEN_NOTALLOWED_PATHS = [
  RouterEnum.Signin.path,
  RouterEnum.Signin.SetIdAndPw.path,
  RouterEnum.Signin.FindId.path,
  RouterEnum.Signin.FindIdDone.path,
  RouterEnum.Signin.ResetPwdAuth.path,
  RouterEnum.Signin.ResetPwd.path,
  RouterEnum.Signin.ResetPwdDone.path,
  RouterEnum.Signup.path,
  RouterEnum.Signup.EnterId.path,
  RouterEnum.Signup.EnterIdDone.path,
  RouterEnum.Signup.SetIdAndPw.path,
  RouterEnum.KakaoTalk.path,
  RouterEnum.GoToApp.path,
  RouterEnum.Maintenance.path, // 서버 점검 페이지 경로 추가
];

router.beforeEach(async (to, from, next) => {
  if (isStudioEmbed()) {
    try {
      getStudioAccessToken();
      const authPaths = [
        RouterEnum.Signin.path, RouterEnum.Signin.SetIdAndPw.path,
        RouterEnum.Signin.FindId.path, RouterEnum.Signin.FindIdDone.path,
        RouterEnum.Signin.ResetPwdAuth.path, RouterEnum.Signin.ResetPwd.path,
        RouterEnum.Signin.ResetPwdDone.path, RouterEnum.Signup.path,
      ];
      if (authPaths.some(path => to.path === path || to.path.startsWith(`${path}/`))) {
        return next(RouterEnum.Dashboard.path);
      }
      // Embedded sessions only accept Studio's verified message channel.
      if (to.query.token) {
        const { token, ...query } = to.query;
        return next({ path: to.path, query, replace: true });
      }
    } catch (error) {
      endStudioSession(error);
      return next(false);
    }
  }
  // 지원 진입·끝·앱 사이 이동 받기는 토큰 없이 열림(화면이 코드를 교환함)
  if (OPEN_ENTRY_PATHS.includes(to.path)) return next();
  // 끝난 지원 탭(뒤로 가기·새로 고침 포함)은 끝 화면 밖으로 가지 않음 — 일반 로그인 토큰으로 넘어가지 않게
  if (isSupportEnded()) return next(RouterEnum.SupportEnded.path);
  // 토큰 기반 자동 로그인 (리셀러 대리 로그인) — 옛 길, 정리 배포까지 그대로(받는 쪽은 지우지 않음)
  const urlToken = to.query.token;
  if (urlToken && typeof urlToken === "string") {
    console.log("[Proxy Login] 토큰 기반 로그인 시작, 기존 세션 초기화");
    // 기존 세션 정보 모두 초기화 (다른 병원 계정으로 전환을 위해)
    // 이 탭이 지원 탭이었다면 그 값도 지우고 일반 탭으로(옛 대리 토큰은 지금처럼 localStorage)
    clearSupport();
    clearTokens(); // access·refresh·증표(증표는 URL 로 넘어오지 않음 → 첫 쓰기에서 step-up)
    removeProfile("userNameHos");
    removeProfile("hospitalNameHos");
    removeProfile("companyImageUrlHos");
    removeProfile("hospitalId");
    removeProfile("hospitalName");
    removeProfile("supportMsgChannel");
    removeProfile("localeHos"); // locale도 초기화 (새 계정의 locale 적용을 위해)
    console.log(
      "[Proxy Login] localStorage 초기화 완료, hospitalNameHos:",
      getProfile("hospitalNameHos")
    );

    // 새 토큰을 localStorage에 저장
    setAccessToken(urlToken);
    console.log("[Proxy Login] 새 토큰 저장 완료, 대시보드로 리다이렉트");

    // 페이지 새로고침으로 Vuex store와 컴포넌트 상태 완전 초기화
    // (SPA 특성상 Vue Router next()로는 컴포넌트가 다시 마운트되지 않음)
    window.location.href = RouterEnum.Dashboard.path;
    return;
  }

  if (to.path === RouterEnum.KakaoTalk.path) {
    let baseUrl = "";
    if (location) {
      if (location.href.includes("localhost")) {
        baseUrl = "http://localhost:5173";
      } else if (location.href.includes("intest-")) {
        baseUrl = "https://intest-app.certi.life";
      } else if (location.href.includes("stg-")) {
        baseUrl = "https://stg-app.certi.life";
      } else {
        baseUrl = "https://app.certi.life";
      }
    }

    window.location.href = `${baseUrl}${to.fullPath}`;
    return;
  }

  // locale 필드 기반 언어 설정 (레거시 데이터 fallback: 'ko')
  const storedLocale = getProfile("localeHos") || "ko";
  i18n.global.locale = storedLocale;
  let locale = storedLocale;

  const accessToken = getAccessToken();

  /*
        case 1. AccessToken 있는데 로그아웃 상태의 페이지 진입 시도 시(비정상, 대시보드로 보냄)
        case 2. AccessToken 있고 로그인 상태의 페이지 진입 시도 시(정상)
        case 3. AccessToken 없고 로그인 상태의 페이지 진입 시도 시(비정상, 로그인 페이지로 보냄)
        case 4. AccessToken 없고 로그아웃 상태의 페이지 진입 시도 시(정상)

    */
  const isAllowedPath = ACCESSTOKEN_NOTALLOWED_PATHS.some((path) =>
    to.path.startsWith(path)
  );

  if (MOBILE_PATHS.some((item) => item.includes(to.path))) {
    return next();
  }
  if (accessToken) {
    if (isAllowedPath) {
      if (to.path === RouterEnum.Signin.ResetPwdDone.path) {
        return next();
      } else {
        return next(RouterEnum.Dashboard.path);
      }
    } else {
      // 사이드바에 안 보이는 화면 주소를 직접 열면 안내 하나 띄우고 대시보드로(10-30 I11 — 화면 틀에 403·로드 실패 알림이 겹쳤음).
      // 판정은 사이드바와 같은 함수(permUi.routeDenial): log 동안은 옛 규칙(회사 정보의 직원 메뉴 권한), enforce 면 새 규칙.
      // 권한 정보를 못 받았으면(회사 정보 실패·introspect 실패) 막지 않음. 대시보드는 표에 없어 늘 열림(되돌아가기 반복 없음)
      await Promise.all([permState.loaded || loadPerms(), store.dispatch("getCompanyInfo")]);
      // 받은 회사 언어로 다시(지원 탭·토큰 진입은 첫 화면 전 localeHos 가 비어 ko 였음 — 로그인 화면은 미리 넣음, 11-01 H5)
      locale = getProfile("localeHos") || locale;
      i18n.global.locale = locale;
      const denial = routeDenialNow(to.path);
      if (denial) {
        // 새 권한 코드면 표준 문구 「권한이 없습니다(필요: 한국어 이름)」, 이름표가 없거나 옛 규칙이면 영문 코드 없이 일반 안내
        const labelOf = denial.codes ? await reloadPerms() : null;
        const named = !!denial.codes && denial.codes.every((c) => labelOf(c) !== c);
        message.warning({
          content: named ? permissionDeniedText({ response: { data: { required: denial.codes } } }, labelOf) : MENU_DENIED_TEXT,
          key: "ROUTE_PERMISSION_DENIED",
        });
        return next(RouterEnum.Dashboard.path);
      }
      if (to.meta.requiresPrimaryChannel) {
        try {
          const response = await alimtalkChannelService.getPrimaryChannel();
          const hasPrimaryChannel = response.success && !!response.data;
          if (!hasPrimaryChannel) {
            alert(
              "알림톡 발송 채널을 먼저 활성화해주세요. 채널 설정 페이지로 이동합니다."
            );
            return next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
          }
        } catch (error) {
          console.error("Failed to check primary channel:", error);
          alert(
            "채널 상태를 확인할 수 없습니다. 채널 설정 페이지로 이동합니다."
          );
          return next({ name: RouterEnum.Alimtalk.ChannelSettings.name });
        }
      }
      return await handleSpecialPaths(to, next, locale);
    }
  } else {
    if (isAllowedPath) {
      return next();
    } else {
      return next(RouterEnum.Signin.path);
    }
  }

  // 서버 점검 모드 확인 (store에서 가져오거나 환경 변수 등에서 설정)
  const isMaintenanceMode = store.state.isMaintenanceMode;

  // 서버 점검 모드일 때 처리 로직 추가
  if (isMaintenanceMode && !to.meta.allowDuringMaintenance) {
    return next(RouterEnum.Maintenance.path);
  }
});

const pathConstraints = {
  /*
        case 1. 비밀번호 입력 안하고 병원 정보 수정 페이지 진입 시도
        case 2. 진료과목에 치과 없는데 치과 관련 페이지 진입 시도
        case 3. 진료과목에 안과 없는데 안과 관련 페이지 진입 시도
        case 4. 진료과목에 성형외과 없는데 성형외과 관련 페이지 진입 시도
    */
  [RouterEnum.InfoEdit.path]: {
    check: () => store.state.isPasswordVerified,
    onFailure: (next) => {
      alert("권한이 없습니다.");
      next(RouterEnum.InfoPassword.path);
    },
  },
  [RouterEnum.Dent.path]: {
    dept: MedicalDept.DENT,
    onFailure: (next) => {
      alert("치과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Oph.path]: {
    dept: MedicalDept.OPH,
    onFailure: (next) => {
      alert("안과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Ps.path]: {
    dept: MedicalDept.PS,
    onFailure: (next) => {
      alert("성형외과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Derm.path]: {
    dept: MedicalDept.DERM,
    onFailure: (next) => {
      alert("피부과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Im.path]: {
    dept: MedicalDept.IM,
    onFailure: (next) => {
      alert("내과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Os.path]: {
    dept: MedicalDept.OS,
    onFailure: (next) => {
      alert("정형외과에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
  [RouterEnum.Vet.path]: {
    dept: MedicalDept.VET_COMMON,
    onFailure: (next) => {
      alert("반려동물에서만 접근 가능합니다.");
      next(RouterEnum.Dashboard.path);
    },
  },
};

async function handleSpecialPaths(to, next, locale) {
  // 회사 정보(getCompanyInfo)는 가드가 권한 판정 앞에서 이미 받음
  const medicalDept = store.state.medicalDept;
  for (const [path, { check, dept, onFailure }] of Object.entries(
    pathConstraints
  )) {
    if (to.path.includes(path)) {
      const isValid = check
        ? check()
        : dept
        ? medicalDept.includes(dept)
        : true;
      if (!isValid) {
        onFailure(next);
        return;
      }

      const { getLocaleRedirect } = localeRouterUtils(locale);
      const localeRedirectPath = getLocaleRedirect(to.path);

      if (localeRedirectPath) {
        // console.log(localeRedirectPath)
        next(localeRedirectPath);
        return;
      }
      break;
    }
  }

  next();
}

router.afterEach((to, from) => {
  // 이용 내역 「화면 위치」(X-Audit-Screen, 표시용)
  setAuditScreen(to);
  // 페이지 전환 후 항상 부드럽게 스크롤을 페이지 맨 위로 이동시킵니다.
  window.scrollTo({ top: 0, behavior: "smooth" });
});

export default router;
